import type { SessionAnalytics } from '@/types/session';
import { KeyLevelsPanel } from './KeyLevelsPanel';
import { LegendPanel } from './LegendPanel';
import { OpenCloseSnapshotPanel } from './OpenCloseSnapshotPanel';
import { PriceDistributionPanel } from './PriceDistributionPanel';
import { SessionPricePathPanel } from './SessionPricePathPanel';
import { getRailPanelState } from './railPanelState.utils';

interface DashboardRightRailProps {
  analytics: SessionAnalytics | null;
  ticker: string;
  sessionDate: string;
  isLoading: boolean;
  error: string | null;
  onRetry: () => void;
}

export function DashboardRightRail({
  analytics,
  ticker,
  sessionDate,
  isLoading,
  error,
  onRetry,
}: DashboardRightRailProps) {
  const request = { analytics, isLoading, error };
  const requestError = analytics == null && !isLoading && error != null;
  const isBusy = analytics == null && !requestError;

  return (
    <aside
      aria-label="Session analytics"
      aria-busy={isBusy}
      className="flex min-w-0 flex-col gap-2 fit:min-h-full"
    >
      {isBusy && <span className="sr-only">Loading session analytics...</span>}
      {requestError && (
        <p role="alert" className="sr-only">
          Session analytics unavailable. {error}
        </p>
      )}
      <PriceDistributionPanel
        state={getRailPanelState(
          request,
          (data) => data.dataAvailability.priceDistribution,
          'Session bars unavailable',
        )}
        sessionDate={sessionDate}
        onRetry={onRetry}
      />
      <SessionPricePathPanel
        state={getRailPanelState(
          request,
          (data) => data.dataAvailability.priceBars,
          'Minute bars unavailable',
        )}
        ticker={ticker}
        sessionDate={sessionDate}
        onRetry={onRetry}
      />
      <OpenCloseSnapshotPanel
        state={getRailPanelState(request)}
        sessionDate={sessionDate}
        onRetry={onRetry}
      />
      <KeyLevelsPanel
        state={getRailPanelState(request)}
        sessionDate={sessionDate}
        onRetry={onRetry}
      />
      <LegendPanel />
    </aside>
  );
}
